import { Component, OnInit } from '@angular/core';
import { LicenseServerService } from '../../../../core/services/license-server.service';

// Interface for the API response
interface LicenseResponse {
  status: string;
  message: string;
  data: any[];
  combinedData: string;
}

@Component({
  selector: 'app-license-summary-view',
  templateUrl: './license-summary-view.component.html',
  styleUrl: './license-summary-view.component.scss'
})
export class LicenseSummaryViewComponent implements OnInit {

  // Component properties
  licenseData: string = '';
  displayData: string = '';
  isLoading: boolean = false;
  isDarkMode: boolean = true; // Default to dark mode
  searchTerm: string = '';
  deploymentName: string = 'default'; // You can get this from route params or input
  error: string = '';

  // Search and display options
  searchPlaceholder: string = 'Find in terminal output...';
  lineNumbers: boolean = true;
  currentMatchIndex: number = 0;
  totalMatches: number = 0;

  // Toast notification
  showToast: boolean = false;
  toastMessage: string = '';

  // Animation states
  copySuccess: boolean = false;
  downloadTriggered: boolean = false;

  constructor(private licenseService: LicenseServerService) {
    // Check for saved theme preference, default to dark
    const savedTheme = localStorage.getItem('license-viewer-theme');
    this.isDarkMode = savedTheme !== 'light'; // Default to dark mode
  }

  ngOnInit(): void {
    this.loadLicenseData();
  }

  /**
   * Load license data from the API
   */
  loadLicenseData(): void {
    this.isLoading = true;
    this.error = '';

    this.licenseService.getLisenceSummary().subscribe({
      next: (response: LicenseResponse) => {
        // Check if the response has the expected structure
        if (response && response.status === 'Success') {
          if (response.combinedData && response.combinedData.trim()) {
            // Store the raw data 
            this.licenseData = response.combinedData;
            this.isLoading = false;
            this.updateSearch(); // This will process line breaks and set displayData
          } else {
            // Handle case where status is success but no license data
            this.licenseData = 'No license data available for this deployment.';
            this.isLoading = false;
            this.updateSearch();
          }
        } else {
          // Handle error status or unexpected response
          this.error = response?.message || 'Failed to retrieve license data';
          this.isLoading = false;
        }
      },
      error: (err) => {
        this.error = 'Failed to load license data: ' + (err.error?.message || err.message || 'Unknown error');
        this.isLoading = false;
        console.error('License data loading error:', err);
      }
    });
  }

  /**
   * Update search highlighting (VS Code style) - FIXED VERSION
   */
  updateSearch(): void {
    if (!this.searchTerm.trim()) {
      this.displayData = this.licenseData.replace(/\\n/g, '\n');
      this.totalMatches = 0;
      this.currentMatchIndex = 0;
      return;
    }

    // Convert \n to actual line breaks before processing
    let processedData = this.licenseData.replace(/\\n/g, '\n');

    // Escape special regex characters
    const escapedSearchTerm = this.escapeRegExp(this.searchTerm);
    const regex = new RegExp(escapedSearchTerm, 'gi');

    // Count total matches
    const matches = processedData.match(regex);
    this.totalMatches = matches ? matches.length : 0;

    // Only reset currentMatchIndex if this is a new search (not navigation)
    if (this.currentMatchIndex === 0 || this.currentMatchIndex > this.totalMatches) {
      this.currentMatchIndex = this.totalMatches > 0 ? 1 : 0;
    }

    // Highlight all matches
    if (this.totalMatches > 0) {
      this.highlightMatches(processedData, regex);
    } else {
      this.displayData = processedData;
    }
  }

  /**
   * Separate method to handle highlighting - ADDED
   */
  private highlightMatches(data: string, regex: RegExp): void {
    let matchCount = 0;
    this.displayData = data.replace(regex, (match) => {
      matchCount++;
      const isCurrentMatch = matchCount === this.currentMatchIndex;
      return `<span class="search-highlight ${isCurrentMatch ? 'current-match' : ''}">${match}</span>`;
    });
  }

  /**
   * Navigate to next search match - IMPROVED
   */
  nextMatch(): void {
    if (this.totalMatches > 0) {
      this.currentMatchIndex = this.currentMatchIndex < this.totalMatches ? this.currentMatchIndex + 1 : 1;
      this.refreshHighlights();
      this.scrollToCurrentMatch();
    }
  }

  /**
   * Navigate to previous search match - IMPROVED
   */
  previousMatch(): void {
    if (this.totalMatches > 0) {
      this.currentMatchIndex = this.currentMatchIndex > 1 ? this.currentMatchIndex - 1 : this.totalMatches;
      this.refreshHighlights();
      this.scrollToCurrentMatch();
    }
  }

  /**
   * Refresh highlights without resetting match index - NEW METHOD
   */
  private refreshHighlights(): void {
    if (!this.searchTerm.trim() || this.totalMatches === 0) {
      return;
    }

    // Convert \n to actual line breaks before processing
    let processedData = this.licenseData.replace(/\\n/g, '\n');

    // Escape special regex characters
    const escapedSearchTerm = this.escapeRegExp(this.searchTerm);
    const regex = new RegExp(escapedSearchTerm, 'gi');

    // Highlight matches with updated current match
    this.highlightMatches(processedData, regex);
  }

  /**
   * Scroll to current match - IMPROVED
   */
  scrollToCurrentMatch(): void {
    setTimeout(() => {
      const currentMatch = document.querySelector('.current-match');
      if (currentMatch) {
        currentMatch.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }
    }, 50); // Reduced timeout for faster response
  }

  /**
   * Escape special regex characters
   */
  escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Handle search input - IMPROVED
   */
  onSearch(): void {
    // Reset match index for new search
    this.currentMatchIndex = 0;
    this.updateSearch();
  }

  /**
   * Clear search and show all data
   */
  clearSearch(): void {
    this.searchTerm = '';
    this.currentMatchIndex = 0;
    this.totalMatches = 0;
    this.updateSearch();
  }

  /**
   * Toggle between dark and light mode
   */
  toggleTheme(): void {
    this.isDarkMode = !this.isDarkMode;
    localStorage.setItem('license-viewer-theme', this.isDarkMode ? 'dark' : 'light');
  }

  /**
   * Refresh license data
   */
  refreshData(): void {
    this.loadLicenseData();
  }

  /**
   * Copy data to clipboard
   */
  copyToClipboard(): void {
    // Copy original data without HTML highlighting
    const cleanData = this.licenseData.replace(/\\n/g, '\n');
    navigator.clipboard.writeText(cleanData).then(() => {
      this.copySuccess = true;
      this.showToastMessage('📋 Copied to clipboard!');

      // Reset copy success state after 2 seconds
      setTimeout(() => {
        this.copySuccess = false;
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy data:', err);
      this.showToastMessage('❌ Failed to copy data');
    });
  }

  /**
   * Show toast notification
   */
  showToastMessage(message: string): void {
    this.toastMessage = message;
    this.showToast = true;

    // Auto-hide after 3 seconds
    setTimeout(() => {
      this.showToast = false;
    }, 3000);
  }

  /**
   * Hide toast notification
   */
  hideToast(): void {
    this.showToast = false;
  }

  /**
   * Download data as text file
   */
  downloadData(): void {
    const cleanData = this.licenseData.replace(/\\n/g, '\n');
    const blob = new Blob([cleanData], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `license-summary-${this.deploymentName}-${new Date().toISOString().slice(0, 10)}.txt`;
    link.click();
    window.URL.revokeObjectURL(url);

    // Show download success message
    this.showToastMessage('💾 File downloaded successfully!');
  }

  /**
   * Get line count for display
   */
  getLineCount(): number {
    const cleanData = this.licenseData.replace(/\\n/g, '\n');
    return cleanData.split('\n').length;
  }

  /**
   * Format data with line numbers if enabled
   */
  getFormattedData(): string {
    // Don't process line breaks here since we handle it in updateSearch
    let dataToFormat = this.displayData;

    if (!this.lineNumbers) {
      return dataToFormat;
    }

    const lines = dataToFormat.split('\n');
    return lines.map((line, index) => {
      const lineNumber = (index + 1).toString().padStart(3, ' ');
      return `${lineNumber} | ${line}`;
    }).join('\n');
  }

  /**
   * Toggle line numbers display
   */
  toggleLineNumbers(): void {
    this.lineNumbers = !this.lineNumbers;
  }
}
